import { Chip, useTheme } from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { FunctionComponent, useContext, useEffect } from 'react';
import { AppContext } from '../../../core/config';
import { useAppTranslate } from '../../../core/hooks';
import { NetworkServiceUpdateEvent } from '../../../l3-services/model';
import { Loader } from '../../../ui/components';
import { useDashboardItsmMaintenanceWorks } from '../../hooks';
import { DashboardCard } from '../DashboardCard/DashboardCard';
import { useLocalizedDateFormat } from '../../../core/hooks/DateFormat/DateFormat';
import { UnorderedList } from '../../../ui/components/UnorderedList/UnorderedList';

interface Props {
  maintenanceWorksUpdates: NetworkServiceUpdateEvent[];
}

const options: MUIDataTableOptions = {
  enableNestedDataAccess: '.',
  selectToolbarPlacement: 'none',
  download: false,
  print: false,
  rowHover: false,
  expandableRows: false,
  selectableRows: 'none',
  expandableRowsHeader: false,
  filter: false,
  search: false,
  viewColumns: false,
  pagination: false,
};

export const DashboardMaintenanceWorks: FunctionComponent<Props> = ({ maintenanceWorksUpdates }) => {
  const translate = useAppTranslate();
  const { customerId } = useContext(AppContext);
  const { data, isFetched, isFetching, refetch } = useDashboardItsmMaintenanceWorks(customerId);
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const localizedDateFormat = useLocalizedDateFormat();
  const renderDate = (date?: string): string => date ? localizedDateFormat.format(new Date(date)) : '-';

  useEffect(() => {
    if (customerId !== null && !isFetching) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceWorksUpdates, refetch]);

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'itsmId',
      label: translate('cards.activeMaintenanceWorks.itsmId'),
    },
    {
      name: 'status',
      label: translate('cards.activeMaintenanceWorks.status'),
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <Chip
            label={data && data[dataIndex].status}
            sx={{ background: '#D7EDFF', color: primaryColor }}
          />;
        }
      }
    },
    {
      name: 'sozOrderNumbers',
      label: translate('cards.activeMaintenanceWorks.sozOrderNumber'),
      options: {
        customBodyRender: (sozOrderNumbers: number[]) => <UnorderedList values={ sozOrderNumbers } />
      }
    },
    {
      name: 'serviceNames',
      label: translate('cards.activeMaintenanceWorks.serviceName'),
      options: {
        customBodyRender: (serviceNames: string[]) => <UnorderedList values={ serviceNames } />
      }
    },
    {
      name: 'startTime',
      label: translate('cards.activeMaintenanceWorks.startTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(data?.[dataIndex].startTime);
        }
      }
    },
    {
      name: 'endTime',
      label: translate('cards.activeMaintenanceWorks.endTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(data?.[dataIndex].endTime);
        }
      }
    }
  ];

  return <DashboardCard title={translate('cards.activeMaintenanceWorks.title')}>
    {isFetching ? (<Loader loading={true} />) : null}
    {!isFetching && isFetched && data ? (
      <MUIDataTable
        title={''}
        columns={columns}
        data={data}
        options={options}
      />
    ) : null}
  </DashboardCard>;
};